import type { ProviderKind, ServerProvider } from "@cmmd-forge/contracts";

import type { Thread } from "../../types";
import { resolveDefaultSelectableProvider, resolveSelectableProvider } from "../../providerModels";
import {
  providerPolicyForAccess,
  type ForgeProviderAccessState,
  type ProviderCredentialMode,
} from "../../cmmd/forgeProviderAccess";
import {
  resolveLockedProviderUseVerdict,
  resolveRunnableProviderSnapshots,
  type ShellProviderAvailability,
} from "../../cmmd/providerAvailabilityJoin";
import { resolveShellProviderAvailabilityRows } from "../../cmmd/useShellProviderAvailability";
import { describeProviderAvailability } from "../../cmmd/providerIneligibilityCopy";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";
import { threadHasStarted } from "../ChatView.composer";

/**
 * The Composer's whole provider decision for the next turn, in one pure module.
 *
 * Everything the composer needs to render and to gate a send composes here:
 * the thread's lock, the person's pick, the project default, the picker rows,
 * and the refusal if the next turn may not start. The two verdicts meet only
 * inside this module; every consumer sees the one answer for its question, so
 * the picker and the send gate cannot disagree the way they did in forge#3385
 * (production 2026-09-16: the composer showed Claude and the turn ran Codex,
 * because the send path judged a second predicate and silently substituted
 * the project default). forge#3393 taught the refusal to keep the selection
 * sticky; forge#3394 moves the judgement onto the join's Run verdict.
 */

/**
 * Why the composer cannot send, in the one field that travels from the plan
 * to the banner and the send gate.
 *
 * `null` sends. A string is a refusal with its sentence. `false` holds the
 * send with deliberately no copy: the authority check is still loading, the
 * state is near-instant, and transient states earn no UI (forge#3394). The
 * distinction is the point: loading and error used to share one
 * sentence-shaped channel, so a held send either showed checking copy or did
 * not hold at all.
 */
export type ComposerDisabledReason = string | false | null;

export interface ComposerTurnPlanInput {
  readonly thread: Thread | null | undefined;
  /** The provider the person picked in the composer, when they picked one. */
  readonly selectedProvider: ProviderKind | null | undefined;
  readonly projectDefaultProvider: ProviderKind | null | undefined;
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
  /**
   * Whether the target Environment has published an inventory at all. Only the
   * picker rows read it. The selection is judged with a live inventory
   * because the composer always holds a concrete providers array: an absent
   * snapshot there is the Environment declining to publish the provider, the
   * fact `hasLiveInventory: true` names.
   */
  readonly hasLiveInventory: boolean;
}

export interface ComposerTurnPlan {
  /** The provider this composer's next turn would run on, judged as-is. */
  readonly selection: ProviderKind;
  readonly lockedProvider: ProviderKind | null;
  readonly threadProvider: ProviderKind | null;
  /** What the picker renders: the Pick verdict for every rail row. */
  readonly pickRows: ReadonlyArray<ModelPickerProviderAvailability>;
  /** The verdict gating the next send: the Run verdict for `selection`. */
  readonly runVerdict: ShellProviderAvailability;
  /**
   * The send gate, rendered: null sends, a string refuses with its sentence,
   * false holds silently while the verdict is still loading.
   */
  readonly refusalReason: ComposerDisabledReason;
  /** Snapshots whose Run verdict is usable: what model resolution draws from. */
  readonly runnableProviders: ReadonlyArray<ServerProvider>;
}

export function resolveComposerTurnPlan(input: ComposerTurnPlanInput): ComposerTurnPlan {
  const threadProvider =
    input.thread?.modelSelection?.provider ?? input.projectDefaultProvider ?? null;
  const selectedProvider = input.selectedProvider ?? null;
  const lockedProvider = deriveLockedProvider({
    thread: input.thread,
    selectedProvider,
    threadProvider,
  });
  const runnableProviders = resolveRunnableProviderSnapshots({
    providers: input.providers,
    access: input.access,
    credentialMode: input.credentialMode,
    hasLiveInventory: input.hasLiveInventory,
  });
  // forge#3393, restated on the Run verdict: the requested provider is judged
  // as-is, never swapped. Only when nobody picked anything AND no default
  // names one does this heal a display name from the runnable list, which is
  // default resolution, not a substitution of anyone's pick.
  const requestedProvider = selectedProvider ?? threadProvider ?? null;
  const unlockedProvider =
    requestedProvider ??
    resolveDefaultSelectableProvider(runnableProviders) ??
    resolveSelectableProvider(input.providers, null);
  const selection = lockedProvider ?? unlockedProvider;

  const runVerdict = resolveLockedProviderUseVerdict({
    providerKind: selection,
    snapshot: input.providers.find((provider) => provider.provider === selection),
    access: input.access,
    credentialMode: input.credentialMode,
    // forge#3588: this used to be hardcoded `true` regardless of where
    // `input.providers` came from, so a stale reading from CMMD's last-known
    // catalog (published while the Environment was asleep, per
    // `lastKnownProviderCatalog.ts`) refused a send that the Environment could
    // have woken up and served. The caller's own inventory source decides
    // whether a Not on this Environment reading counts as confirmed.
    hasLiveInventory: input.hasLiveInventory,
  }).run;

  return {
    selection,
    lockedProvider,
    threadProvider,
    pickRows: resolveShellProviderAvailabilityRows({
      providers: input.providers,
      policy: providerPolicyForAccess(input.access),
      access: input.access,
      credentialMode: input.credentialMode,
      hasLiveInventory: input.hasLiveInventory,
    }),
    runVerdict,
    refusalReason: refusalReason(selection, runVerdict, lockedProvider !== null),
    runnableProviders,
  };
}

export function deriveLockedProvider(input: {
  thread: Thread | null | undefined;
  selectedProvider: ProviderKind | null;
  threadProvider: ProviderKind | null;
}): ProviderKind | null {
  if (!threadHasStarted(input.thread)) {
    return null;
  }
  return input.thread?.session?.provider ?? input.threadProvider ?? input.selectedProvider ?? null;
}

/**
 * The send gate, rendered.
 *
 * Usable sends. A withheld or errored Run verdict refuses with its sentence,
 * prefixed for an unlocked selection so it reads as what happened to the turn
 * (the forge#3393 copy, kept exactly). A loading verdict holds the send and
 * renders nothing: the check is near-instant and transient states earn no UI,
 * while a check that failed keeps its sentence, because a failure is not
 * transient.
 */
function refusalReason(
  provider: ProviderKind,
  verdict: ShellProviderAvailability,
  locked: boolean,
): ComposerDisabledReason {
  if (verdict.state === "usable") return null;
  if (verdict.state === "unresolved" && verdict.cause === "loading") return false;
  const sentence = describeProviderAvailability(provider, verdict, locked ? "locked" : "unlocked");
  if (sentence === null) return null;
  if (locked) return sentence;
  return verdict.state === "withheld" ? `This turn was not sent: ${sentence}` : sentence;
}
